import styles from './index.less';
import * as React from 'react';
import { observer } from 'mobx-react';
import useStore from '@src/utils/hooks/use-store';
import Page4Store from '@src/pages/page4/store';
import Page from '@src/layout-components/page';
import $$tracker from '@src/tracker';
import { GAME_OFFICIAL_WEBSITE } from '@src/constants/urls';
import HeaderActions from '@src/ui-components/header-actions';

const designImage = require('./images/page4-design-base.jpg');
const neutralSideNavImage = require('./images/page4-side-nav-neutral.png');
const neutralFirstTabImage = require('./images/page4-first-tab-neutral.png');
const sideNavImages = [
  require('./images/page4-nav-item-1.png'),
  require('./images/page4-nav-item-2.png'),
  require('./images/page4-nav-item-3.png'),
  require('./images/page4-nav-item-4.png'),
  require('./images/page4-nav-item-5.png'),
  require('./images/page4-nav-item-6.png'),
  require('./images/page4-nav-item-7.png'),
  require('./images/page4-nav-item-8.png'),
  require('./images/page4-nav-item-9.png'),
  require('./images/page4-nav-item-10.png'),
  require('./images/page4-nav-item-11.png'),
  require('./images/page4-nav-item-12.png')
];
const expandButtonImage = require('./images/page4-design-side-nav-expand.png');
const collapseButtonImage = require('./images/page4-design-side-nav-collapse.png');

const SCROLL_THRESHOLD = 700;
const isMobileViewport = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const screenWidth = window.screen && window.screen.width;
  return (
    screenWidth < 450 ||
    screenWidth / (window.devicePixelRatio || 1) < 450 ||
    window.outerWidth < 450 ||
    window.innerWidth < 450 ||
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  );
};
// Use the ES5 conversion supported by legacy IE pages.
const toArray = nodes => Array.prototype.slice.call(nodes);

const pageTabs = [
  { href: '#/', label: '返回首页', left: 415, width: 178 },
  { href: '#/page1', label: '铁浮城战 焕新重启', trackerTitle: '铁浮城战', left: 593, width: 166 },
  { href: '#/page2', label: '天王觉醒 再耀锋芒', trackerTitle: '天王觉醒', left: 759, width: 164 },
  { href: '#/page3', label: '珍材现世 打宝赚金', trackerTitle: '珍材现世', left: 923, width: 166 },
  { href: '#/page4', label: '十八周年 盛馈江湖', trackerTitle: '十八周年', left: 1089, width: 166 },
  { href: '#/page5', label: '周年特献 首个生态服', trackerTitle: '周年特献', left: 1255, width: 205 }
];

const trackPageTab = tab => {
  if (tab.trackerTitle) {
    $$tracker.clickMenu(tab.trackerTitle);
  }
};

const sections = [
  { label: '抽180万金币', top: 1368, left: 525, navTop: 158 },
  { label: '送VIP大件', top: 2577, left: 703, navTop: 229 },
  { label: '送周年外装', top: 3738, left: 877, navTop: 295 },
  { label: '享超低折扣', top: 4309, left: 1057, navTop: 359 },
  { label: '领超多豪礼', top: 4865, left: 1234, navTop: 423 },
  { label: '特享好礼', top: 5085, navTop: 492 },
  { label: '抖音侠影', top: 5554, navTop: 531 },
  { label: '江湖共贺', top: 7084, navTop: 569 },
  { label: '发财小鸡', top: 7466, navTop: 607 },
  { label: '庆典蛋糕', top: 8378, navTop: 645 },
  { label: '珍馐雅味', top: 9093, navTop: 683 },
  { label: '月度活动', top: 11788, navTop: 721 }
];

const toHorizontalPercent = value => `${(value / 1920) * 100}%`;
const toVerticalPercent = value => `${(value / 13721) * 100}%`;

const Page4 = () => {
  const store = useStore(() => new Page4Store());
  const sectionRefs = React.useRef([]);
  const cmsContentRef = React.useRef(null);
  const [activeSection, setActiveSection] = React.useState(-1);
  const [isMobile, setIsMobile] = React.useState(isMobileViewport);
  const [isPastThreshold, setIsPastThreshold] = React.useState(false);
  const [isSideNavCollapsed, setIsSideNavCollapsed] = React.useState(false);

  const getSectionTargets = React.useCallback(
    () => {
      if (store.contentHtml && cmsContentRef.current) {
        const headings = toArray(cmsContentRef.current.querySelectorAll('h2'));
        // Match activity names because editors can change numbering without changing the section.
        return sections.map((section, index) => {
          if (index < 5) return cmsContentRef.current.querySelector(`a[id="${index + 1}"], a[name="${index + 1}"]`);
          return headings.filter(heading => heading.textContent.replace(/\s/g, '').indexOf(section.label) !== -1)[0];
        });
      }
      return sectionRefs.current;
    },
    [store.contentHtml]
  );

  React.useEffect(
    () => {
      const onScroll = () => {
        setIsPastThreshold(window.pageYOffset > SCROLL_THRESHOLD);

        // Loading images can temporarily shift anchors; the hero must remain unselected.
        if (window.pageYOffset <= 0) {
          setActiveSection(-1);
          return;
        }

        let nextActive = -1;
        const isAtBottom = window.pageYOffset + window.innerHeight >= document.documentElement.scrollHeight - 1;
        getSectionTargets().forEach((section, index) => {
          if (!section) return;
          // Short sections must not activate the next item before its heading reaches the top.
          // At the document end, the final heading may not be able to reach the top.
          const top = section.getBoundingClientRect().top;
          if (top <= 1 || (isAtBottom && top < window.innerHeight)) nextActive = index;
        });
        setActiveSection(nextActive);
      };

      onScroll();
      const onResize = () => {
        setIsMobile(isMobileViewport());
        onScroll();
      };
      const cmsContent = cmsContentRef.current;
      // Late-loading CMS images move headings even if the user has not scrolled.
      if (cmsContent) cmsContent.addEventListener('load', onScroll, true);
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onResize);
      return () => {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onResize);
        if (cmsContent) cmsContent.removeEventListener('load', onScroll, true);
      };
    },
    [getSectionTargets]
  );

  const setSectionRef = index => node => {
    sectionRefs.current[index] = node;
  };

  const scrollToSection = index => {
    const section = getSectionTargets()[index];
    if (!section) return;
    const top = section.getBoundingClientRect().top + window.pageYOffset;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  const showSideNav = isMobile || (isPastThreshold && !isSideNavCollapsed);
  const showExpandButton = !isMobile && isPastThreshold && isSideNavCollapsed;
  const navPosition = (top, height) => ({ top: `${(top / 975) * 100}%`, height: `${(height / 975) * 100}%` });
  const sectionTargets = getSectionTargets();

  return (
    <Page className={styles.root}>
      <main className={styles.designCanvas}>
        <div className={styles.designBackdrop}>
          <HeaderActions onWebsiteClick={() => $$tracker.clickGoWebsite()} onDownloadClick={() => $$tracker.clickDownload()} />
          <a
            className={styles.officialLogo}
            href={GAME_OFFICIAL_WEBSITE}
            aria-label="剑侠世界官网"
            onClick={() => $$tracker.clickGoWebsite()}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className={styles.srOnly}>剑侠世界官网</span>
          </a>
          <img className={styles.designImage} src={designImage} alt="十八周年盛馈江湖活动资料" />
          {activeSection !== 0 ? (
            <img
              className={styles.neutralFirstTab}
              src={neutralFirstTabImage}
              alt=""
              aria-hidden="true"
              style={{ left: toHorizontalPercent(469), top: toVerticalPercent(1112), width: toHorizontalPercent(281) }}
            />
          ) : null}

          <nav className={styles.pageNav} aria-label="资料片页面导航">
            {pageTabs.map(tab => (
              <a
                href={tab.href}
                key={tab.href}
                style={{ left: toHorizontalPercent(tab.left), width: toHorizontalPercent(tab.width) }}
                aria-current={tab.href === '#/page4' ? 'page' : undefined}
                onClick={() => trackPageTab(tab)}
              >
                <span className={styles.srOnly}>{tab.label}</span>
              </a>
            ))}
          </nav>

          <nav className={styles.sectionNav} aria-label="内容章节">
            {sections.slice(0, 5).map((section, index) => (
              <button
                type="button"
                key={section.label}
                style={{ left: toHorizontalPercent(section.left) }}
                aria-current={activeSection === index ? 'location' : undefined}
                onClick={() => scrollToSection(index)}
              >
                <span className={styles.srOnly}>{section.label}</span>
              </button>
            ))}
          </nav>

          {sections.map((section, index) => (
            <span
              className={styles.sectionAnchor}
              key={section.label}
              ref={setSectionRef(index)}
              style={{ top: toVerticalPercent(section.top) }}
              aria-hidden="true"
            />
          ))}
        </div>

        {/* Official CMS HTML is trusted first-party publisher content. */}
        {store.contentHtml ? <section ref={cmsContentRef} className={styles.cmsContent} dangerouslySetInnerHTML={{ __html: store.contentHtml }} /> : null}
      </main>

      <aside className={`${styles.sideNav} ${showSideNav ? styles.isVisible : styles.isHidden}`} aria-label="页内导航" aria-hidden={!showSideNav}>
        <img src={activeSection < 0 ? neutralSideNavImage : sideNavImages[activeSection]} alt="" aria-hidden="true" />
        <button
          type="button"
          className={styles.collapseSideNav}
          style={navPosition(80, 60)}
          onClick={() => !isMobile && setIsSideNavCollapsed(true)}
          tabIndex={showSideNav ? 0 : -1}
        >
          <img src={collapseButtonImage} alt="" aria-hidden="true" />
          <span className={styles.srOnly}>收起菜单</span>
        </button>
        {sections.map((section, index) => (
          <button
            type="button"
            className={index >= 5 ? styles.textSection : undefined}
            style={navPosition(section.navTop, index < 5 ? 64 : 38)}
            key={section.label}
            aria-current={activeSection === index ? 'location' : undefined}
            onClick={() => scrollToSection(index)}
            disabled={Boolean(store.contentHtml) && !sectionTargets[index]}
            tabIndex={showSideNav && (!store.contentHtml || sectionTargets[index]) ? 0 : -1}
          >
            <span className={styles.srOnly}>{section.label}</span>
          </button>
        ))}
        <button
          type="button"
          className={styles.backTop}
          style={navPosition(780, 88)}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          tabIndex={showSideNav ? 0 : -1}
        >
          <span className={styles.srOnly}>返回顶部</span>
        </button>
        <a className={styles.backHome} href="#/" tabIndex={showSideNav ? 0 : -1} style={navPosition(879, 88)}>
          <span className={styles.srOnly}>返回首页</span>
        </a>
      </aside>

      <button
        type="button"
        className={`${styles.expandSideNav} ${showExpandButton ? styles.isVisible : styles.isHidden}`}
        onClick={() => setIsSideNavCollapsed(false)}
        tabIndex={showExpandButton ? 0 : -1}
        aria-hidden={!showExpandButton}
      >
        <img src={expandButtonImage} alt="" aria-hidden="true" />
        <span className={styles.srOnly}>展开菜单</span>
      </button>
    </Page>
  );
};

export default observer(Page4);
